import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EXTRA_USER_GROSS_MONTH_CENTS, EXTRA_USER_MAX_PER_BOOKING, formatCents, type SeatOverview } from '@kontor/shared';
import { api, ApiError } from '../api';
import './seats.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const day = (iso: string) => dateFormat.format(new Date(`${iso}T12:00:00`));
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

const STEPS = ['Anzahl', 'Kosten', 'Bestätigen'] as const;

/**
 * Benutzerlizenzen kaufen (Änderungswunsch vom 21.10.2026): Der Kauf läuft in einem Fenster mit drei Schritten (Anzahl, Kosten, Bestätigen) und erklärt alles, was dazugehört: was ein
 * Zusatzbenutzer ist und kostet, wie die erste Rechnung entsteht, wie die Buchung weiterläuft und wie sie sich kündigen lässt. Gebucht wird erst nach dem Haken im letzten Schritt.
 * Buchen verlangt das Recht `setup.billing` und ein bezahltes Paket, beides prüft der Server, das Fenster erklärt es vorher.
 */
export function SeatsBookDialog({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const [step, setStep] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [confirm, setConfirm] = useState(false);
  const [done, setDone] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seats = useQuery({ queryKey: ['seats'], queryFn: async () => (await api<{ data: SeatOverview }>('GET', '/billing/seats')).data });

  useEffect(() => {
    const el = dialog.current;
    if (el && !el.open) el.showModal();
  }, []);

  const book = useMutation({
    mutationFn: () => api('POST', '/billing/seats', { quantity, confirm }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['seats'] });
      client.invalidateQueries({ queryKey: ['users'] });
      client.invalidateQueries({ queryKey: ['billing'] });
      setError(null);
      setDone(quantity);
    },
    onError: (e) => setError(errorText(e, 'Die Buchung hat nicht geklappt.')),
  });

  const data = seats.data;
  const extra = data ? data.total - data.included : 0;
  const total = quantity * EXTRA_USER_GROSS_MONTH_CENTS;

  return (
    <dialog
      ref={dialog}
      className="dialog dialog--wide"
      aria-labelledby="seats-dialog-title"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialog.current && !book.isPending) onClose();
      }}
    >
      <div className="dialog__body seats-dialog">
        <h2 id="seats-dialog-title">Benutzerlizenzen kaufen</h2>
        {done === null && (
          <ol className="seats-dialog__steps" aria-label="Schritte">
            {STEPS.map((label, i) => (
              <li key={label} className={i === step ? 'is-current' : i < step ? 'is-done' : undefined} aria-current={i === step ? 'step' : undefined}>
                <span className="seats-dialog__num" aria-hidden="true">{i + 1}</span> {label}
              </li>
            ))}
          </ol>
        )}

        {seats.isLoading && <p className="muted">Wird geladen …</p>}
        {seats.isError && <div className="alert alert--error" role="alert">Die Plätze konnten nicht geladen werden.</div>}
        {data && !data.canBook && (
          <>
            <div className="alert alert--warn" role="status">Zusatzbenutzer lassen sich buchen, sobald Ihr Paket bestellt und bezahlt ist. Solange Sie Wirkia testen, bestellen Sie zuerst ein Paket unter Einrichtung → Wirkia Konto.</div>
            <div className="form-actions"><button className="btn btn--secondary" type="button" onClick={onClose}>Schließen</button></div>
          </>
        )}

        {data && data.canBook && done !== null && (
          <>
            <div className="alert alert--ok" role="status">
              <strong>{done} Zusatzbenutzer gebucht.</strong> Die Plätze stehen sofort zur Verfügung. Die erste Rechnung kommt per E-Mail.
            </div>
            <p>Jetzt können Sie die neuen Benutzer einladen. Jeder bekommt einen Link und legt sein Passwort selbst fest.</p>
            <div className="form-actions">
              <Link className="btn btn--primary" to="/einrichtung/benutzer/neu" onClick={onClose}>Benutzer einladen</Link>
              <button className="btn btn--secondary" type="button" onClick={onClose}>Fertig</button>
            </div>
          </>
        )}

        {data && data.canBook && done === null && step === 0 && (
          <>
            <p>
              In Ihrem Paket {data.included === 1 ? 'ist ein Benutzer' : `sind ${data.included} Benutzer`} enthalten{extra > 0 ? `, dazu haben Sie ${extra} Zusatzbenutzer gebucht` : ''}. Zurzeit sind {data.used} von {data.total} Plätzen belegt.
              Jeder weitere Benutzer kostet <strong>{formatCents(EXTRA_USER_GROSS_MONTH_CENTS)} brutto im Monat</strong>, unabhängig vom Paket.
            </p>
            <label className="field">
              <span>Wie viele Zusatzbenutzer möchten Sie buchen?</span>
              <input className="input" type="number" min={1} max={EXTRA_USER_MAX_PER_BOOKING} value={quantity} onChange={(e) => setQuantity(Math.max(1, Math.min(EXTRA_USER_MAX_PER_BOOKING, Number(e.target.value) || 1)))} />
              <span className="muted small">Höchstens {EXTRA_USER_MAX_PER_BOOKING} je Buchung. Weitere buchen Sie danach in einer zweiten Buchung.</span>
            </label>
            <p className="seats__sum">{quantity} × {formatCents(EXTRA_USER_GROSS_MONTH_CENTS)} = <strong>{formatCents(total)} brutto im Monat</strong></p>
            <div className="form-actions">
              <button className="btn btn--primary" type="button" onClick={() => setStep(1)}>Weiter</button>
              <button className="btn btn--secondary" type="button" onClick={onClose}>Abbrechen</button>
            </div>
          </>
        )}

        {data && data.canBook && done === null && step === 1 && (
          <>
            <h3>So entsteht Ihre Rechnung</h3>
            <dl className="seats-dialog__facts">
              <dt>Monatlicher Preis</dt>
              <dd>{quantity} × {formatCents(EXTRA_USER_GROSS_MONTH_CENTS)} = <strong>{formatCents(total)} brutto</strong></dd>
              {data.cycle && (
                <>
                  <dt>Erste Rechnung</dt>
                  <dd>
                    {data.cycle.days < data.cycle.totalDays ? (
                      <>
                        Nur diese Buchung, anteilig bis zum {day(data.cycle.firstEnd)}: {data.cycle.days} von {data.cycle.totalDays} Tagen, also <strong>{formatCents(quantity * data.cycle.firstGrossPerUnitCents)} brutto</strong> ({formatCents(data.cycle.firstGrossPerUnitCents)} je Benutzer).
                      </>
                    ) : (
                      <>Nur diese Buchung, für den ganzen Monat bis zum {day(data.cycle.firstEnd)}: <strong>{formatCents(total)} brutto</strong>.</>
                    )}
                  </dd>
                  <dt>Danach</dt>
                  <dd>Ab dem {day(data.cycle.nextStart)} läuft die Buchung im Zyklus Ihres Pakets weiter. Jeden Monat stellen wir eine Rechnung mit allen Ihren Zusatzbuchungen aus.</dd>
                </>
              )}
              <dt>Die Plätze</dt>
              <dd>Sie stehen <strong>sofort</strong> zur Verfügung, noch vor der Zahlung.</dd>
              <dt>Kündigen</dt>
              <dd>Jederzeit unter Einrichtung → Wirkia Konto → Plätze und Zusatzbenutzer. Die Kündigung gilt zum Ende des bezahlten Monats, bis dahin bleiben die Plätze. Danach dürfen nicht mehr Benutzer aktiv sein, als Ihr Paket Plätze hat.</dd>
              <dt>Rechnung</dt>
              <dd>Sie kommt per E-Mail als E-Rechnung (PDF). Alle Rechnungen finden Sie unter Einrichtung → Wirkia Konto.</dd>
            </dl>
            <div className="form-actions">
              <button className="btn btn--primary" type="button" onClick={() => setStep(2)}>Weiter</button>
              <button className="btn btn--secondary" type="button" onClick={() => setStep(0)}>Zurück</button>
            </div>
          </>
        )}

        {data && data.canBook && done === null && step === 2 && (
          <>
            <h3>Verbindlich buchen</h3>
            <p>
              Sie buchen <strong>{quantity} Zusatzbenutzer</strong> für <strong>{formatCents(total)} brutto im Monat</strong>.
              {data.cycle ? ` Die erste Rechnung über ${formatCents(quantity * data.cycle.firstGrossPerUnitCents)} brutto kommt per E-Mail.` : ''}
            </p>
            <label className="check">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
              <span>Ich buche {quantity} Zusatzbenutzer zahlungspflichtig. Die Rechnung kommt per E-Mail, die Buchung läuft monatlich im Zyklus meines Pakets weiter, bis ich sie kündige.</span>
            </label>
            {error && <div className="alert alert--error" role="alert">{error}</div>}
            <div className="form-actions">
              <button className="btn btn--primary" type="button" disabled={book.isPending || !confirm} onClick={() => { setError(null); book.mutate(); }}>
                {book.isPending ? 'Wird gebucht …' : 'Zahlungspflichtig buchen'}
              </button>
              <button className="btn btn--secondary" type="button" disabled={book.isPending} onClick={() => setStep(1)}>Zurück</button>
            </div>
          </>
        )}
      </div>
    </dialog>
  );
}
